import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  Card,
  CardContent,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  CalendarMonth as CalendarIcon,
  FilterList as FilterIcon,
  AccountBalance,
} from '@mui/icons-material';
import { format, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../services/api';

interface Transacao {
  transactionId: string;
  tipo: string;
  valor: string;
  data: string;
  dataLote?: string;
  descricao: string;
  numeroDocumento?: string;
  cpfCnpj?: string;
  descInfComplementar?: string;
}

interface ExtratoData {
  saldoAtual: string;
  saldoBloqueado: string;
  saldoLimite: string;
  saldoAnterior: string;
  transacoes: Transacao[];
}

const StatementsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [extrato, setExtrato] = useState<ExtratoData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [ano, setAno] = useState(now.getFullYear());
  const [diaInicial, setDiaInicial] = useState<number | ''>('');
  const [diaFinal, setDiaFinal] = useState<number | ''>('');

  const fetchExtrato = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: any = {};
      if (diaInicial) params.diaInicial = diaInicial;
      if (diaFinal) params.diaFinal = diaFinal;

      const response = await api.get(`/conta-corrente/extrato/${mes}/${ano}`, { params });

      const success = response?.success ?? response?.data?.success;
      const data = response?.data ?? response;

      if (success) {
        setExtrato(data?.data || data);
      } else {
        setError(data?.error?.message || 'Erro ao carregar extrato');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.error?.message || err.message || 'Erro ao carregar extrato');
    } finally {
      setLoading(false);
    }
  }, [mes, ano, diaInicial, diaFinal]);

  useEffect(() => {
    fetchExtrato();
  }, []);

  const handleSearch = () => {
    fetchExtrato();
  };

  const formatCurrency = (value: string | number | undefined) => {
    if (value === undefined || value === null) return 'R$ 0,00';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(numValue);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return format(date, 'dd/MM/yyyy', { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const isCredit = (tipo: string) => {
    return tipo?.toUpperCase() === 'C' || tipo?.toUpperCase() === 'CREDITO';
  };

  const getMonthName = (month: number) => {
    const months = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    return months[month - 1] || '';
  };

  // Calculate totals
  const totais = {
    creditos: 0,
    debitos: 0,
  };

  extrato?.transacoes?.forEach(t => {
    const valor = parseFloat(t.valor) || 0;
    if (isCredit(t.tipo)) {
      totais.creditos += valor;
    } else {
      totais.debitos += valor;
    }
  });

  if (!extrato && !loading && !error) {
    return (
      <Box>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
          <AccountBalance sx={{ mr: 1, verticalAlign: 'middle' }} />
          Extrato da Conta
        </Typography>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <AccountBalance sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Dados não disponíveis
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure a integração Sicoob para visualizar o extrato.
          </Typography>
          <Button variant="contained" href="/settings/sicoob">
            Configurar Integração
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            <AccountBalance sx={{ mr: 1, verticalAlign: 'middle' }} />
            Extrato da Conta
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Consulte as movimentações da sua conta corrente
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar">
            <IconButton onClick={fetchExtrato} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Período de Consulta
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Mês"
              value={mes}
              onChange={(e) => setMes(Number(e.target.value))}
              SelectProps={{ native: true }}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {getMonthName(i + 1)}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Ano"
              value={ano}
              onChange={(e) => setAno(Number(e.target.value))}
              SelectProps={{ native: true }}
            >
              {Array.from({ length: 5 }, (_, i) => {
                const year = now.getFullYear() - i;
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                );
              })}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Dia Inicial"
              type="number"
              value={diaInicial}
              onChange={(e) => setDiaInicial(e.target.value ? Number(e.target.value) : '')}
              inputProps={{ min: 1, max: 31 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Dia Final"
              type="number"
              value={diaFinal}
              onChange={(e) => setDiaFinal(e.target.value ? Number(e.target.value) : '')}
              inputProps={{ min: 1, max: 31 }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FilterIcon />}
              sx={{ height: '40px' }}
            >
              Consultar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Saldos */}
      {extrato && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Saldo Anterior
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {formatCurrency(extrato.saldoAnterior)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Saldo Atual
                </Typography>
                <Typography variant="h6" fontWeight={600} color="primary">
                  {formatCurrency(extrato.saldoAtual)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Total Créditos
                </Typography>
                <Typography variant="h6" fontWeight={600} color="success.main">
                  {formatCurrency(totais.creditos)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Total Débitos
                </Typography>
                <Typography variant="h6" fontWeight={600} color="error.main">
                  {formatCurrency(totais.debitos)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabela de Transações */}
      <Paper>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Movimentações - {getMonthName(mes)} {ano}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {extrato?.transacoes?.length || 0} transações
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Documento</TableCell>
                <TableCell align="center">Tipo</TableCell>
                <TableCell align="right">Valor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : !extrato?.transacoes?.length ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nenhuma transação encontrada no período
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                extrato.transacoes.map((transacao, index) => (
                  <TableRow key={transacao.transactionId || index} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(transacao.data)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {transacao.descricao}
                      </Typography>
                      {transacao.descInfComplementar && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {transacao.descInfComplementar}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {transacao.numeroDocumento || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {isCredit(transacao.tipo) ? (
                        <Chip
                          size="small"
                          label="Crédito"
                          color="success"
                          icon={<ArrowDownwardIcon />}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label="Débito"
                          color="error"
                          icon={<ArrowUpwardIcon />}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={isCredit(transacao.tipo) ? 'success.main' : 'error.main'}
                      >
                        {isCredit(transacao.tipo) ? '+' : '-'} {formatCurrency(transacao.valor)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default StatementsPage;
